<template>
  <div id="campfire-root">
    <mdb-mask class="overflow-auto gp-full-overlay" overlay="stylish-strong">
      <mdb-container class="gp-has-header">
        <div v-if="game && game.isHost && !terminated" aria-label="Show Edit Tools"
             class="gp-fixed-action-btn"
             title="Show Host Tools">
          <a class="btn-floating btn-warning ripple-parent" @click.prevent="showHostTools = true">
            <mdb-icon fal icon="cogs"/>
          </a>
        </div>
        <mdb-card v-if="showError" class="mb-4">
          <mdb-card-header class="primary-color text-white">
            <h3>Oops!</h3>
          </mdb-card-header>
          <mdb-card-body class="text-center">
            <h3>{{ errorMessage }}</h3>
          </mdb-card-body>
        </mdb-card>

        <mdb-card v-if="connectionFailed" class="mb-4">
          <mdb-card-header class="primary-color text-white">
            <h3>{{ game.name }}</h3>
          </mdb-card-header>
          <mdb-card-body>
            <h4>ERROR</h4>
            {{ connectionMessage }}
          </mdb-card-body>
        </mdb-card>

        <mdb-card v-if="terminated" class="mb-4">
          <mdb-card-header class="primary-color-dark text-white">
            <h3>⚠ Game Over</h3>
          </mdb-card-header>
          <mdb-card-body>
            <scoreboard :game="game" :currentState="currentState"/>
          </mdb-card-body>
        </mdb-card>

        <mdb-card v-if="connectionClosed && !terminated" class="mb-4">
          <mdb-card-header class="primary-color text-white">
            <h3>Connection Closed</h3>
          </mdb-card-header>
          <mdb-card-body>
            <div>It looks like your connection to the game server has timed out.</div>
            <div>
              <mdb-btn icon="play" @click="loadGame">Reconnect</mdb-btn>
            </div>
          </mdb-card-body>
        </mdb-card>

        <mdb-card v-if="game && !connectionConfirmed && !connectionFailed" class="mb-4">
          <mdb-card-body>
            <h3>{{ game.name }}</h3>
            <div class="text-info">Hosted by {{ game.host }}</div>
            <mdb-spinner/>
            <h2>One Moment...connecting to game.</h2>
            <div class="mt-4">
              <avatar :avatar="game.groupAvatar" container-class="text-center"/>
              <h3>{{ game.groupName }}</h3>
            </div>
          </mdb-card-body>
        </mdb-card>

        <assembler v-if="!terminated && currentState && currentState.currentStatus == 'Assembling Friends'"
                   :current-state="currentState"
                   :game="game"/>

        <q-and-a-play v-if="!terminated && currentState && game && (game.playModule == 'qa')"
                      :current-state="currentState"
                      @setStatus="setStatus"
                      :game="game"/>

        <ichi-play v-if="!terminated && currentState && game && (game.playModule == 'ichi')"
                      :current-state="currentState"
                      @setStatus="setStatus"
                      :game="game"/>

        <timed-question-play v-if="!terminated && currentState && game && (game.playModule == 'timedq')"
                      :current-state="currentState"
                      @setStatus="setStatus"
                      :game="game"/>

        <tutti-play v-if="!terminated && currentState && game && (game.playModule == 'tutti')"
                             :current-state="currentState"
                             @setStatus="setStatus"
                             :game="game"/>

        <buscando-play v-if="!terminated && currentState && game && (game.playModule == 'buscando')"
                    :current-state="currentState"
                    @setStatus="setStatus"
                    :game="game"/>

        <hangman-play v-if="!terminated && currentState && game && (game.playModule == 'hangman')"
                       :current-state="currentState"
                       @setStatus="setStatus"
                       :game="game"/>

        <mdb-modal v-if="showGuestModal"
                   class="text-center" size="sm">
          <mdb-modal-header :close="false" center class="secondary-color text-white">
            <h3>Welcome, Guest. Please enter your name.</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <mdb-input id="newName" v-model="newName"
                       active label="Name"
                       @keydown.native="newNameKeyDown"
            />
            <mdb-btn :disabled="!newName" @click="setName">
              <mdb-icon icon="save"/>
              Save
            </mdb-btn>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal v-if="passwordDialog"
                   class="text-center" size="sm">
          <mdb-modal-header :close="false" center class="secondary-color text-white">
            <h3>Game Code Required</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <mdb-input id="password" v-model="gameCode"
                       active label="Code"
                       @keydown.native="passwordKeyDown"
            />
            <mdb-btn :disabled="!gameCode" @click="loadGame">
              <mdb-icon icon="check"/>
              Join
            </mdb-btn>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal v-if="showHostTools && !showSwitchGame" @close="showHostTools = false">
          <mdb-modal-header :close="true" class="secondary-color text-white"><h4>Game Tools</h4></mdb-modal-header>
          <mdb-modal-body>
            <mdb-btn icon="stop" @click="endRequested=true">End Game</mdb-btn>
            <mdb-btn icon="user-plus" @click="setStatus('Assembling Friends')">Let More People Join</mdb-btn>
            <mdb-btn icon="recycle" @click="showSwitchGame=true">Switch Game</mdb-btn>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal v-if="showSwitchGame" @close="showSwitchGame = false">
          <mdb-modal-header :close="true" class="secondary-color text-white"><h4>Switch Game</h4></mdb-modal-header>
          <mdb-modal-body>
            <start-dialog :isChange="true" @newCampfire="changeGame" @cancelStart="showSwitchGame=false"/>
          </mdb-modal-body>
        </mdb-modal>

        <yes-no-modal v-if="endRequested" @yes="endSession" @no="endRequested=false" icon="minus-circle"/>

      </mdb-container>
    </mdb-mask>
  </div>
</template>
<script>
import api from '../../services/groupsPlaceApi.js'
import Avatar from "../common/Avatar";
import {
  mdbBadge,
  mdbBtn,
  mdbBtnFixed,
  mdbBtnFixedItem,
  mdbCard,
  mdbCardBody,
  mdbCardFooter,
  mdbCardHeader,
  mdbChipInput,
  mdbCol,
  mdbContainer,
  mdbIcon,
  mdbInput,
  mdbJumbotron,
  mdbMask,
  mdbModal,
  mdbModalBody,
  mdbModalHeader,
  mdbModalTitle,
  mdbRow,
  mdbSpinner
} from 'mdbvue';
import YesNoModal from "../common/YesNoModal";
import Vue from "vue";
import IchiPlay from "./IchiPlay";
import Assembler from "./Assembler";
import WordCards from "./WordCards";
import WordCard from "./WordCard";
import QAndAPlay from "./QAndAPlay";
import StartDialog from "./StartDialog";
import TimedQuestionPlay from "./TimedQuestionPlay";
import Scoreboard from "./Scoreboard";
import TuttiPlay from "./TuttiPlay";
import BuscandoPlay from "./BuscandoPlay";
import HangmanPlay from "./HangmanPlay";

export default {
  name: "campfire-player",
  components: {
    YesNoModal,
    mdbContainer,
    mdbBtn,
    mdbCard,
    mdbCardHeader,
    mdbCardBody,
    mdbCardFooter,
    mdbIcon,
    mdbInput,
    mdbModal,
    mdbModalHeader,
    mdbModalBody,
    mdbModalTitle,
    mdbChipInput,
    mdbMask,
    mdbBadge,
    mdbSpinner,
    mdbJumbotron,
    mdbBtnFixed,
    mdbBtnFixedItem,
    mdbRow,
    mdbCol,
    Avatar,
    IchiPlay,
    Assembler,
    WordCards,
    WordCard,
    QAndAPlay,
    StartDialog,
    TimedQuestionPlay,
    TuttiPlay,
    BuscandoPlay,
    HangmanPlay,
    Scoreboard
  },
  computed: {
    loggedIn() {
      return this.$store.state.loggedIn
    },
    currentUser() {
      return this.$store.state.userDetails
    },
    settings() {
      return this.$store.state.settings
    },
    lastError() {
      if (!this.game) return null
      else return this.game.lastError
    },
    answersDone() {
      let ans = true
      for (let p of this.currentState.players) {
        if (!p.answered && p.playerId != this.currentState.moderator) ans = false
      }
      return ans
    },
  },
  watch: {
    lastError(n) {
      if (n) this.handleLastError()
    }
  },
  data() {
    return {
      connectionClosed: false,
      game: null,
      socketConnection: null,
      connectionConfirmed: false,
      connectionFailed: false,
      connectionMessage: null,
      currentState: {currentStatus: "", openToUser: false, players: []},
      showHostTools: false,
      exitModal: false,
      terminated: false,
      gameCode: null,
      passwordDialog: false,
      showButtons: false,
      showGuestModal: false,
      endRequested: false,
      newName: "",
      myAnswer: "",
      showError: false,
      errorMessage: null,
      quickLink: "",
      showSwitchGame: false
    }
  },
  mounted() {
    if (this.settings && this.settings.staticImageUrl) this.setBackground()
    else setTimeout(() => {
      this.setBackground()
    }, 5000)

    if (this.$route.query.code) {
      this.gameCode = this.$route.query.code
    }
    this.loadGame()
  },
  beforeRouteLeave(to, from, next) {
    this.terminated = true
    if (this.socketConnection) this.socketConnection.close()
    next()
    return
  },
  methods: {
    handleLastError() {
      this.$notify.error({message: this.game.lastError, timeOut: 3000})
      this.game.lastError = null
    },
    createQuickLink() {
      this.$store.dispatch("setExecuting", true)
      api.post("/presentationViewer/quickLink/" + this.$route.params.sessionId, {
        token: this.game.accessToken, linkCode: this.quickLink
      }).then(
        () => {
          this.presentation.quickLink = this.quickLink
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })

    },
    cancelQuickLink() {
      this.$store.dispatch("setExecuting", true)
      api.post("/presentationViewer/cancelQuickLink/" + this.$route.params.sessionId, {
        token: this.game.accessToken
      }).then(
        () => {
          this.presentation.quickLink = null
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })

    },
    handleAnswerKeydown(e) {
      if (e.keyCode == 13 && this.myAnswer) this.selectAnswer(this.myAnswer)
    },
    buttonsHover() {
      this.showButtons = true;
    },
    buttonsHoverOut() {
      this.showButtons = false;
    },
    newGameSelected(game) {
      this.showNavigator = false
      this.$store.dispatch("setExecuting", true)
      let url = "/presentation/switchPresentation/" + this.$route.params.sessionId
      api.post(url, {presentation: pres.id}).then(
        (results) => {

        },
        (error) => {
          this.$notify.error({messageText: error.response.data, timeOut: 3000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })

    },
    loadGame() {
      this.passwordDialog = false
      this.connectionClosed = false
      this.$store.dispatch("setExecuting", true)
      let lastToken = localStorage.getItem("lastCampfireToken")
      let lastGuestName = localStorage.getItem("lastGuestName")
      let url = "/campfirePlayer/start/" + this.$route.params.sessionId
      if (lastToken) url += "?lastToken=" + lastToken
      api.post(url, {gameCode: this.gameCode, name: lastGuestName, lastToken: lastToken}).then(
        (results) => {
          this.game = results.data
          if (results.data.accessToken) localStorage.setItem("lastCampfireToken", results.data.accessToken)
          else localStorage.removeItem("lastCampfireToken")
          this.connectSocket()
          if (this.game.isGuest && this.game.participationName == 'Guest') {
            this.showGuestModal = true
            Vue.nextTick().then(function () {
              let el = document.getElementById("newName")
              if (el) el.focus()
            })
          }
        },
        (error) => {
          if (error.response.status == 401) {
            this.passwordDialog = true
          } else {
            this.errorMessage = error.response.data
            this.showError = true
          }
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    ignoreClick() {
      return
    },
    connectSocket() {
      if (!this.game.socketsURL) {
        this.connectionFailed = true
        this.connectionMessage = "A configuration is missing to enable communications for this service."
        return
      }
      try {
        this.socketConnection = new WebSocket("wss://" + this.game.socketsURL)
      } catch (e) {
        this.connectionFailed = true
        this.connectionMessage = "Could not connect to campfire services. " + e
        return
      }

      this.socketConnection.onmessage = (event) => {
        let eventData = JSON.parse(event.data)
        if (eventData.action == "getConnectionId") {
          api.post("/campfirePlayer/storeSocketConnection/" + this.$route.params.sessionId, {
            token: this.game.accessToken,
            connectionId: eventData.connectionId
          }).then(
            (results) => {
              this.connectionConfirmed = true
              this.currentState = results.data
            },
            (error) => {
              this.$notify.error({message: error.response.data, timeOut: 5000})
            })
        } else {
          this.handleSocketMessage(eventData)
        }
      }

      this.socketConnection.onclose = (event) => {
        this.connectionClosed = true
      }

      this.socketConnection.onopen = (event) => {
        let payload = {action: "sendmessage", innerAction: "confirmConnection", data: ""}
        this.socketConnection.send(JSON.stringify(payload))
      }
    },
    setBackground() {
      let el = document.getElementById("campfire-root")
      if (el) {
        el.setAttribute("style", "background-image: url('" + this.settings.staticImageUrl + "/campfire.jpg');")
      }
    },
    handleSocketMessage(event) {
      if (!this.currentState) return
      if (event.innerAction == "userConnected") {
        let loc = -1
        for (let i = 0; i < this.currentState.players.length; i++) {
          if (this.currentState.players[i].playerId == event.player.playerId) {
            loc = i
          }
        }
        if (loc == -1) {
          this.currentState.players.push(event.player)
          if (event.player.name != "Guest") {
            this.$notify.success({
                                   message: event.player.name + " has joined.",
                                   timeOut: 1500,
                                   position: "bottom center"
                                 })
          }
        }
      }
      if (event.innerAction == "userDisconnected") {
        if (event.playerId == this.currentState.myPlayerId) {
          this.$router.push("/")
          return
        }
        let loc = -1
        for (let i = 0; i < this.currentState.players.length; i++) {
          if (this.currentState.players[i].playerId == event.playerId) {
            this.$notify.warn({
                                message: this.currentState.players[i].name + " has left.",
                                timeOut: 1500,
                                position: "bottom center"
                              })
            loc = i
          }
        }
        if (loc >= 0) this.currentState.players.splice(loc, 1)
      }
      if (event.innerAction == "playerAnswered") {
        for (let i = 0; i < this.currentState.players.length; i++) {
          let player = this.currentState.players[i]
          if (player.playerId == event.playerId) {
            player.answered = true
            player.currentAnswer = event.currentAnswer
          }
        }
      }
      if (event.innerAction == "playerVoted") {
        for (let i = 0; i < this.currentState.players.length; i++) {
          let player = this.currentState.players[i]
          if (player.playerId == event.playerId) {
            player.voted = true
          }
        }
      }

      if (event.innerAction == "newCards") {
        setTimeout(() => {this.releaseACard(event.cards)},400)
      }

      if (event.innerAction == "guestNameChange") {
        let loc = -1
        for (let i = 0; i < this.currentState.players.length; i++) {
          if (this.currentState.players[i].playerId == event.playerId) {
            loc = i
            this.currentState.players[i].name = event.name
            this.$notify.success({message: event.name + " has joined.", timeOut: 1500, position: "bottom center"})
          }
        }
      }
      if (event.innerAction == "userAnswers") {
        for (let a of event.answers) {
          for (let p of this.currentState.players) {
            if (a.playerId == p.playerId) {
              p.currentAnswer = a.currentAnswer
            }
          }
        }

      }

      if (event.innerAction == "sessionTerminated") {
        localStorage.removeItem("lastCampfireToken")
        this.socketConnection.close()
        this.terminated = true
        //this.game = null
        //this.currentState = null
        this.currentState.iAmModerator = false
      }
      if (event.innerAction == "gameSwitched") {
        location.reload()
      }

      if (event.innerAction == "setStatus") {
        this.currentState.currentStatus = event.status
        if (event.status == "Reviewing Answers") this.removeSelectedCard()

        if (event.details) {
          const keys = Object.keys(event.details);
          keys.forEach(( key, index) => {
            if (key == "moderator") {
              if (event.details[key] === this.currentState.myPlayerId) this.currentState.iAmModerator = true
              else this.currentState.iAmModerator = false
            }
            if (key == "hotSeatPlayerId") {
              if (event.details[key] == this.currentState.myPlayerId) this.currentState.iAmInHotSeat = true
              else this.currentState.iAmInHotSeat = false
            }
            this.currentState[key] = event.details[key]
          })
        }

      }
    },
    newNameKeyDown(e) {
      if (e.keyCode == 13 && this.newName) this.setName()
    },
    passwordKeyDown(e) {
      if (e.keyCode == 13 && this.gameCode) this.loadGame()
    },
    releaseACard(cards) {
      //slow the adding of cards to make it obvious which are the new ones
      let card = cards.pop()
      this.currentState.myCards.push(card)
      if (cards.length>0) setTimeout(() => {this.releaseACard(cards)},400)
    },
    removeSelectedCard() {
      let i=0, remove=-1;
      for (let card of this.currentState.myCards) {
        if (card.selected) remove = i
        i++
      }
      if (remove>=0) this.currentState.myCards.splice(remove,1)
    },
    setName() {
      localStorage.setItem("lastGuestName", this.newName)
      this.$store.dispatch("setExecuting", true)
      this.game.participationName = this.newName
      this.showGuestModal = false
      api.post("/campfirePlayer/setName/" + this.$route.params.sessionId, {
        token: this.game.accessToken,
        name: this.newName
      }).then(
        () => {

        },
        (error) => {
          this.showGuestModal = true
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    terminateSession() {
      this.exitModal = false
      this.controls.terminateSession(this.game)
      this.game = null
      this.currentState = null
      this.terminated = true
      this.socketConnection.close()
    },
    setStatus(status) {
      this.$store.dispatch("setExecuting", true)
      api.post("/campfirePlayer/setStatus/" + this.game.sessionId, {
        status: status,
        accessToken: this.game.accessToken
      }).then(
        (results) => {
          //this.currentState.currentStatus = results.data.status
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)

      })
    },
    changeGame(newGame) {
      this.showHostTools = false
      this.showSwitchGame = false
      this.$store.dispatch("setExecuting", true)
      api.post("/campfirePlayer/switchGame/" + this.game.sessionId, {
        accessToken: this.game.accessToken,
        newGame
      }).then(
        (results) => {

        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)

      })
    },
    endSession() {
      this.endRequested = false
      this.showHostTools = false
      this.$store.dispatch("setExecuting", true)
      api.post("/campfirePlayer/endSession/" + this.game.sessionId, {
              accessToken: this.game.accessToken
      }).then(
        (results) => {

        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)

      })
    },

  }
};
</script>
<style scoped>
@media screen and (max-width: 499px ) {
  .gp-fixed-action-btn {
    position: fixed;
    right: 5px;
    bottom: 10px;
    z-index: 998;
    margin-bottom: 0;
    overflow: hidden;
    height: 100px;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: end;
    -ms-flex-align: end;
    align-items: flex-end;
  }
}

@media screen and (min-width: 500px ) {

  .gp-fixed-action-btn {
    position: fixed;
    right: 35px;
    top: 100px;
    z-index: 998;
    margin-bottom: 0;
    overflow: hidden;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: end;
    -ms-flex-align: end;
    align-items: flex-end;
    padding: 15px 15px 15px 15px;
    padding-bottom: 15px;
    padding-left: 15px;
    padding-right: 15px;
  }
}
</style>
